import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import PhotoMedia from "@/components/PhotoMedia";
import { ArrowLeft, Music, Play, Video, Heart } from "lucide-react";
import { toast } from "sonner";

const SoundPage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const [sound, setSound] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingKey, setPlayingKey] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/feed/sound/${encodeURIComponent(key)}`);
      setSound(data.sound);
      setPosts(data.posts);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [key]);

  useEffect(() => { load(); return () => { if (audioRef.current) audioRef.current.pause(); }; }, [load]);

  const togglePreview = () => {
    if (!sound?.preview_url) return;
    if (playingKey) { audioRef.current?.pause(); setPlayingKey(false); return; }
    const a = new Audio(sound.preview_url);
    a.onended = () => setPlayingKey(false);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingKey(true);
  };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="sound-back">
        <ArrowLeft className="h-4 w-4" /> Retour
      </button>

      {/* Sound header */}
      <div className="flex items-center gap-4" data-testid="sound-header">
        <button onClick={togglePreview} className="relative h-20 w-20 rounded-2xl overflow-hidden bg-secondary grid place-items-center shrink-0" data-testid="sound-preview-toggle" aria-label="Écouter l'extrait">
          {sound?.artwork_url ? <img src={sound.artwork_url} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <Music className="h-8 w-8 text-primary" />}
          {sound?.preview_url && (
            <span className="relative z-10 h-9 w-9 rounded-full bg-black/55 grid place-items-center text-white">
              <Play className={`h-4 w-4 ${playingKey ? "opacity-40" : "fill-white"}`} />
            </span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-display text-xl truncate flex items-center gap-1.5"><Music className="h-4 w-4 text-primary" /> {sound?.title || key}</p>
          {sound?.artist && <p className="text-sm text-muted-foreground truncate">{sound.artist}</p>}
          <p className="text-xs text-muted-foreground mt-1">{posts.length} vidéo(s)</p>
        </div>
      </div>

      <Button className="w-full rounded-xl neon-glow-blue-sm" onClick={() => navigate("/envoyer")} data-testid="sound-use-button">
        <Video className="h-4 w-4 mr-2" /> Utiliser ce son
      </Button>

      {/* Videos using this sound */}
      {loading ? (
        <div className="grid grid-cols-3 gap-2">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="aspect-[9/16] rounded-xl" />)}</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Music className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">Aucune vidéo avec ce son</p>
          <p className="text-sm mt-1">Soyez le premier à l'utiliser !</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2" data-testid="sound-videos-grid">
          {posts.map((p) => (
            <button key={p.id} onClick={() => navigate("/decouvrir")} className="relative aspect-[9/16] rounded-xl overflow-hidden bg-secondary" data-testid={`sound-video-${p.id}`}>
              {p.cover ? (
                <img src={p.cover} alt={p.caption || ""} className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <PhotoMedia photo={p} />
              )}
              <div className="absolute bottom-1 left-1 flex items-center gap-1 text-[10px] text-white drop-shadow">
                <Heart className="h-3 w-3 fill-white" /> {p.like_count || 0}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SoundPage;
